import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, SpinnerGap, Cube, Robot, Files, PencilSimple, FloppyDisk, ArrowsClockwise, CaretRight, CaretDown, File, FileTs, FileJs, FileMd, Plugs, UsersThree, Slideshow, ShieldCheck, Trash, Plus, X, ChartBar, Blueprint, Monitor, CheckCircle, XCircle, Key } from '@phosphor-icons/react';
import * as api from '../api/client';
import { apiFetch } from '../api/auth-fetch';

// ── Types ────────────────────────────────────────────────────────────────

interface TalentFile {
  path?: string;
  name?: string;
  size: number;
  modified?: string;
}

interface Talent {
  name: string;
  category: string;
  display_name: string;
  files: TalentFile[];
}

interface TulpaTalent {
  id: string;
  name: string;
  display_name: string;
  category: string;
}

interface Tulpa {
  id: string;
  name: string;
  engine_name: string;
  is_active: boolean;
  talents: TulpaTalent[];
}

// ── API helpers ──────────────────────────────────────────────────────────

const adminRequest = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const res = await apiFetch(`/api/admin${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  const data = await res.json();
  return data.data ?? data;
};

// ── Main Component ───────────────────────────────────────────────────────

type AdminTab = 'dashboard' | 'reports' | 'memory' | 'config' | 'connections' | 'credentials' | 'tulpas' | 'agents' | 'talents' | 'users' | 'decks' | 'templates' | 'system';

interface AdminDashboardProps {
  onBack: () => void;
}

export default function AdminDashboard({ onBack }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');

  type SidebarItem =
    | { type: 'tab'; key: AdminTab; label: string; icon: React.ReactNode }
    | { type: 'section'; label: string };

  // Tulpas + Talents are CF-native (workspace-scoped CRUD against
  // /api/tulpas and /api/talents). The legacy arx-style Connections,
  // Credentials, Agents, Templates tabs stay hidden — those need
  // arx server concepts the CF rebuild doesn't have an analog for.
  const sidebarItems: SidebarItem[] = [
    { type: 'tab', key: 'dashboard', label: 'Dashboard', icon: <ChartBar size={18} weight="light" /> },
    { type: 'tab', key: 'reports', label: 'Reports', icon: <ChartBar size={18} weight="light" /> },
    { type: 'tab', key: 'memory', label: 'Memory', icon: <Files size={18} weight="light" /> },
    { type: 'section', label: 'CONFIGURATION' },
    { type: 'tab', key: 'config', label: 'Configuration', icon: <Cube size={18} weight="light" /> },
    { type: 'tab', key: 'connections', label: 'Connections', icon: <Plugs size={18} weight="light" /> },
    { type: 'section', label: 'AGENT' },
    { type: 'tab', key: 'tulpas', label: 'Tulpas', icon: <Robot size={18} weight="light" /> },
    { type: 'tab', key: 'talents', label: 'Talents', icon: <Cube size={18} weight="light" /> },
    { type: 'section', label: 'BUSINESS' },
    { type: 'tab', key: 'users', label: 'Users', icon: <UsersThree size={18} weight="light" /> },
    { type: 'tab', key: 'decks', label: 'Decks', icon: <Slideshow size={18} weight="light" /> },
    { type: 'section', label: 'SYSTEM' },
    { type: 'tab', key: 'system', label: 'System', icon: <Monitor size={18} weight="light" /> },
  ];

  return (
    <div style={{ height: 'var(--app-height)', display: 'flex', background: 'var(--bg-primary)' }}>
      {/* Sidebar */}
      <div style={{
        width: 220, borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', background: 'var(--bg-sidebar)',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <button onClick={onBack} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', padding: 0, display: 'flex',
          }}>
            <ArrowLeft size={18} weight="light" />
          </button>
          <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
            Admin
          </span>
        </div>
        <nav style={{ padding: '8px 0', flex: 1 }}>
          {sidebarItems.map((item, idx) => {
            if (item.type === 'section') {
              return (
                <div
                  key={`section-${idx}`}
                  style={{
                    padding: '12px 20px 4px',
                    fontSize: 'var(--fs-2xs)',
                    fontWeight: 700,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    userSelect: 'none',
                  }}
                >
                  {item.label}
                </div>
              );
            }
            return (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 20px', border: 'none', cursor: 'pointer',
                  background: activeTab === item.key ? 'var(--bg-accent, rgba(0,51,255,0.08))' : 'none',
                  color: activeTab === item.key ? 'var(--brand, #0033FF)' : 'var(--sidebar-text)',
                  fontSize: 'var(--fs-sm)', fontFamily: 'inherit', textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
        {activeTab === 'dashboard' && <DashboardOverview />}
        {activeTab === 'reports' && <ReportsManager />}
        {activeTab === 'memory' && <MemoryManager />}
        {activeTab === 'connections' && <ConnectionsManager />}
        {activeTab === 'config' && <ConfigurationManager />}
        {activeTab === 'credentials' && <CredentialsManager />}
        {activeTab === 'users' && <UsersManager />}
        {activeTab === 'agents' && <AgentsManager />}
        {activeTab === 'decks' && <DecksManager />}
        {activeTab === 'talents' && <TalentManager />}
        {activeTab === 'tulpas' && <TulpaManager />}
        {activeTab === 'templates' && <TemplatesManager />}
        {activeTab === 'system' && <SystemManager />}
      </div>
    </div>
  );
}

// ── Shared hooks ─────────────────────────────────────────────────────────

// The CF rebuild does not stash workspace_id on `pp_user` localStorage —
// users can belong to multiple workspaces, and the chat flow resolves
// workspace from the deck. The admin's per-workspace managers
// (TulpaManager, TalentManager) need a workspace context, so we fetch
// the user's workspaces once and pick the first as the active one. A
// real workspace picker can replace this when more than one workspace
// per user becomes common.
function usePrimaryWorkspaceId(): { wsId: string | null; ready: boolean } {
  const [wsId, setWsId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/workspaces');
        if (!res.ok) throw new Error(`workspaces fetch ${res.status}`);
        const body = (await res.json()) as { workspaces?: { id: string }[] };
        const first = body.workspaces?.[0]?.id ?? null;
        if (!cancelled) setWsId(first);
      } catch {
        if (!cancelled) setWsId(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { wsId, ready };
}

// ── Shared Styles ────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: '20px 24px',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--fs-sm)',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)',
  border: '1px solid var(--border)', borderRadius: 8,
  background: 'var(--bg-primary)', color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s',
  boxSizing: 'border-box' as const,
};

// ── Dashboard Overview ───────────────────────────────────────────────────

interface StatsData {
  users: number;
  workspaces?: number;
  decks: number;
  decks_by_phase: Record<string, number>;
  decks_this_week: number;
  active_sessions?: number;
  active_tulpas?: number;
  active_agents?: number;
  generated_at?: number;
  sources?: Record<string, string>;
}

const PHASE_COLOURS: Record<string, string> = {
  gather: '#3b82f6',
  plan: '#3b82f6',
  'plan-ready': '#ca8a04',
  plan_ready: '#ca8a04',
  preview: '#22c55e',
  rehearse: '#14b8a6',
  exported: '#8b5cf6',
};

const PHASE_FALLBACK_COLOURS = ['#64748b', '#f59e0b', '#06b6d4', '#ec4899', '#84cc16', '#a855f7'];

function formatPhaseLabel(phase: string): string {
  if (!phase || phase === '(unknown)') return 'Unknown';
  return phase
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function phaseColour(phase: string, index: number): string {
  return PHASE_COLOURS[phase] ?? PHASE_FALLBACK_COLOURS[index % PHASE_FALLBACK_COLOURS.length];
}

function relativeTime(ts?: number): string {
  if (!ts) return 'just now';
  const delta = Math.max(0, Date.now() - ts);
  if (delta < 10_000) return 'just now';
  if (delta < 60_000) return `${Math.round(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.round(delta / 60_000)}m ago`;
  return new Date(ts).toLocaleString();
}

function DashboardOverview() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadStats = useCallback(async () => {
    setRefreshing(true);
    setError('');
    try {
      const raw = await adminRequest<{ stats: StatsData } | StatsData>('GET', '/stats');
      const nextStats: StatsData = (raw as { stats?: StatsData })?.stats ?? (raw as StatsData);
      setStats(nextStats);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { void loadStats(); }, [loadStats]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading stats...
      </div>
    );
  }

  if (error || !stats) {
    return <div style={{ color: '#ef4444' }}>Error: {error || 'No data'}</div>;
  }

  const phaseEntries = Object.entries(stats.decks_by_phase ?? {});
  const totalPhase = phaseEntries.reduce((sum, [, n]) => sum + (n || 0), 0);

  const statCards: { label: string; value: number | string; accent: string; sourceKey: string }[] = [
    { label: 'Total Users', value: stats.users, accent: '#3b82f6', sourceKey: 'users' },
    { label: 'Total Decks', value: stats.decks, accent: 'var(--brand, #0033FF)', sourceKey: 'decks' },
    { label: 'Active Tulpas', value: stats.active_tulpas ?? '-', accent: '#22c55e', sourceKey: 'active_tulpas' },
    { label: 'Decks This Week', value: stats.decks_this_week, accent: '#8b5cf6', sourceKey: 'decks_this_week' },
  ];

  const phaseItems = phaseEntries
    .sort((a, b) => b[1] - a[1])
    .map(([key, value], index) => ({
      key,
      label: formatPhaseLabel(key),
      value,
      color: phaseColour(key, index),
    }));

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>
            Dashboard
          </h2>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            Updated {relativeTime(stats.generated_at)}
          </div>
        </div>
        <button
          onClick={() => void loadStats()}
          disabled={refreshing}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', color: 'var(--text-secondary)',
            fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: refreshing ? 'wait' : 'pointer',
            fontFamily: 'inherit', opacity: refreshing ? 0.65 : 1,
          }}
        >
          <ArrowsClockwise size={15} weight="bold" className={refreshing ? 'animate-spin' : undefined} />
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 16, marginBottom: 32 }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            ...sectionStyle,
            display: 'flex', flexDirection: 'column',
            padding: '18px 16px',
          }}>
            <div style={{
              fontSize: 'var(--fs-xl)', fontWeight: 700, color: card.accent,
              lineHeight: 1.2, marginBottom: 6,
            }}>
              {card.value}
            </div>
            <div style={{
              fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
            }}>
              {card.label}
            </div>
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 7 }}>
              {stats.sources?.[card.sourceKey] ?? 'D1'}
            </div>
          </div>
        ))}
      </div>

      {/* Phase Distribution */}
      <div style={sectionStyle}>
        <div style={{ ...labelStyle, marginBottom: 16 }}>Decks by Phase</div>

        {totalPhase === 0 ? (
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>No decks yet</div>
        ) : (
          <>
            {/* Horizontal bar */}
            <div style={{
              display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden',
              background: 'var(--bg-primary)', marginBottom: 16,
            }}>
              {phaseItems.map(item => {
                const pct = totalPhase > 0 ? (item.value / totalPhase) * 100 : 0;
                if (pct === 0) return null;
                return (
                  <div
                    key={item.key}
                    title={`${item.label}: ${item.value} (${Math.round(pct)}%)`}
                    style={{
                      width: `${pct}%`, background: item.color, minWidth: pct > 0 ? 2 : 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'width 0.3s ease',
                    }}
                  >
                    {pct > 15 && (
                      <span style={{ fontSize: 'var(--fs-2xs)', color: 'white', fontWeight: 600 }}>
                        {item.value}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {phaseItems.map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }} />
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                    {item.label} ({item.value})
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reports Manager ──────────────────────────────────────────────────────
//
// Weekly / monthly usage telemetry table. Aligns PP main #131. Reads
// /api/admin/reports?period=weekly|monthly. Pure-aggregation endpoint
// — totals + top_users[10] + top_decks[10]. No snapshot table at the
// current scale; if events/day grows past ~100k we'll add a nightly
// cron.

interface ReportTotals {
  active_users: number;
  decks_created: number;
  messages_sent: number;
  views: number;
  share_views: number;
  exports: number;
}

interface ReportTopUser {
  user_id: string;
  display_name: string;
  email: string | null;
  messages: number;
  decks: number;
  opens: number;
}

interface ReportTopDeck {
  deck_id: string;
  title: string;
  owner_id: string;
  owner_name: string;
  opens: number;
  share_views: number;
  exports: number;
  messages: number;
}

interface ReportData {
  period: 'weekly' | 'monthly';
  start_ts: number;
  end_ts: number;
  totals: ReportTotals;
  top_users: ReportTopUser[];
  top_decks: ReportTopDeck[];
}

function ReportsManager() {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await adminRequest<{ report: ReportData } | ReportData>(
          'GET',
          `/reports?period=${period}`,
        );
        if (cancelled) return;
        // Accept both legacy shape (raw report) and wrapper shape.
        const report =
          (r as any)?.report ?? (r as ReportData | null);
        setData(report);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const fmtDate = (ts: number) => new Date(ts).toLocaleDateString();

  const totalCards = data
    ? [
        { label: 'Active Users', value: data.totals.active_users, accent: '#3b82f6' },
        { label: 'Decks Created', value: data.totals.decks_created, accent: 'var(--brand, #0033FF)' },
        { label: 'Messages Sent', value: data.totals.messages_sent, accent: '#22c55e' },
        { label: 'Deck Opens', value: data.totals.views, accent: '#8b5cf6' },
        { label: 'Share Views', value: data.totals.share_views, accent: '#ca8a04' },
        { label: 'Exports', value: data.totals.exports, accent: '#ef4444' },
      ]
    : [];

  const tdCell: React.CSSProperties = {
    padding: '8px 14px',
    fontSize: 'var(--fs-sm)',
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border)',
  };
  const tdNum: React.CSSProperties = {
    ...tdCell,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  };
  const thCell: React.CSSProperties = {
    padding: '10px 14px',
    textAlign: 'left',
    color: 'var(--text-muted)',
    fontWeight: 600,
    fontSize: 'var(--fs-xs)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg-primary)',
  };
  const thNum: React.CSSProperties = { ...thCell, textAlign: 'right' };

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          Reports
        </h2>
        <div style={{ display: 'flex', gap: 4, border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
          {(['weekly', 'monthly'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '4px 14px',
                fontSize: 'var(--fs-sm)',
                fontWeight: 500,
                background: period === p ? 'var(--bg-accent, rgba(0,51,255,0.08))' : 'transparent',
                color: period === p ? 'var(--brand, #0033FF)' : 'var(--text-secondary)',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textTransform: 'capitalize',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
          <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading report…
        </div>
      )}
      {error && !data && <div style={{ color: '#ef4444' }}>Error: {error}</div>}

      {data && (
        <>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 16 }}>
            {fmtDate(data.start_ts)} — {fmtDate(data.end_ts)}
          </div>

          {/* Totals */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 12, marginBottom: 28,
          }}>
            {totalCards.map((card) => (
              <div key={card.label} style={{
                ...sectionStyle,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '16px 12px', textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 'var(--fs-xl)', fontWeight: 700, color: card.accent,
                  lineHeight: 1.2, marginBottom: 4,
                }}>
                  {card.value}
                </div>
                <div style={{
                  fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600,
                }}>
                  {card.label}
                </div>
              </div>
            ))}
          </div>

          {/* Top users */}
          <h3 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: 8 }}>
            Top users
          </h3>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 28 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thCell}>Name</th>
                  <th style={thCell}>Email</th>
                  <th style={thNum}>Messages</th>
                  <th style={thNum}>Decks</th>
                  <th style={thNum}>Opens</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...tdCell, textAlign: 'center', color: 'var(--text-muted)' }}>No activity in this window.</td></tr>
                ) : (
                  data.top_users.map((u) => (
                    <tr key={u.user_id}>
                      <td style={tdCell}>{u.display_name}</td>
                      <td style={{ ...tdCell, fontSize: 'var(--fs-xs)' }}>{u.email ?? '—'}</td>
                      <td style={tdNum}>{u.messages}</td>
                      <td style={tdNum}>{u.decks}</td>
                      <td style={tdNum}>{u.opens}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Top decks */}
          <h3 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, marginBottom: 8 }}>
            Top decks
          </h3>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thCell}>Deck</th>
                  <th style={thCell}>Owner</th>
                  <th style={thNum}>Opens</th>
                  <th style={thNum}>Share Views</th>
                  <th style={thNum}>Exports</th>
                  <th style={thNum}>Messages</th>
                </tr>
              </thead>
              <tbody>
                {data.top_decks.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...tdCell, textAlign: 'center', color: 'var(--text-muted)' }}>No activity in this window.</td></tr>
                ) : (
                  data.top_decks.map((d) => (
                    <tr key={d.deck_id}>
                      <td style={tdCell}>{d.title}</td>
                      <td style={tdCell}>{d.owner_name}</td>
                      <td style={tdNum}>{d.opens}</td>
                      <td style={tdNum}>{d.share_views}</td>
                      <td style={tdNum}>{d.exports}</td>
                      <td style={tdNum}>{d.messages}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Connections Manager ──────────────────────────────────────────────────

// ── Memory Manager ──────────────────────────────────────────────────────
//
// Browse + edit the four memory scopes (task / user / workspace / org).
// Aligns PP main #141. PP-CF stores natively in D1 (scoped_memory
// table) via /api/admin/memory/*. Two-pane layout: scope-picker +
// id-picker on the left, file list + editor on the right.

type MemoryScope = 'task' | 'user' | 'workspace' | 'org';

interface MemoryListEntry {
  key: string;
  size: number;
  content_type: string;
  updated_at: number;
}

interface MemoryFile extends MemoryListEntry {
  value: string;
}

interface AdminUserLite {
  id: string;
  email: string | null;
  display_name: string;
}

interface AdminWorkspaceLite {
  id: string;
  name: string;
  owner_id: string;
  created_at: number;
  deck_count: number;
}

function MemoryManager() {
  const [scope, setScope] = useState<MemoryScope>('user');
  const [scopeId, setScopeId] = useState<string>('');
  const [manualId, setManualId] = useState<string>('');
  const [users, setUsers] = useState<AdminUserLite[]>([]);
  const [workspaces, setWorkspaces] = useState<AdminWorkspaceLite[]>([]);
  const [decks, setDecks] = useState<{ id: string; title: string }[]>([]);
  const [entries, setEntries] = useState<MemoryListEntry[]>([]);
  const [activeFile, setActiveFile] = useState<MemoryFile | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [newKey, setNewKey] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Boot: load scope-picker sources (users / workspaces / decks)
  useEffect(() => {
    (async () => {
      try {
        const [u, w, d] = await Promise.all([
          adminRequest<AdminUserLite[] | { users: AdminUserLite[] }>('GET', '/users'),
          adminRequest<{ workspaces: AdminWorkspaceLite[] }>('GET', '/memory/workspaces'),
          adminRequest<AdminDeck[] | { decks: AdminDeck[] }>('GET', '/decks'),
        ]);
        setUsers(Array.isArray(u) ? u : (u?.users ?? []));
        setWorkspaces(w?.workspaces ?? []);
        const dlist = Array.isArray(d) ? d : (d?.decks ?? []);
        setDecks(dlist.map((x: any) => ({ id: x.id, title: x.title || '(untitled)' })));
      } catch (e: any) {
        setError(e?.message || 'Failed to load scope sources');
      }
    })();
  }, []);

  const refreshList = useCallback(async (s: MemoryScope, id: string) => {
    if (!id) {
      setEntries([]);
      setActiveFile(null);
      return;
    }
    setLoading(true);
    setError(null);
    setActiveFile(null);
    try {
      const r = await adminRequest<{ entries: MemoryListEntry[] }>(
        'GET',
        `/memory/scopes/${encodeURIComponent(s)}/${encodeURIComponent(id)}`,
      );
      setEntries(r.entries || []);
    } catch (e: any) {
      setError(e?.message || 'List failed');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // When scope/scopeId change → refresh
  useEffect(() => {
    void refreshList(scope, scopeId);
  }, [scope, scopeId, refreshList]);

  const openFile = async (key: string) => {
    setError(null);
    try {
      const r = await adminRequest<MemoryFile>(
        'GET',
        `/memory/scopes/${encodeURIComponent(scope)}/${encodeURIComponent(scopeId)}/${encodeURIComponent(key)}`,
      );
      setActiveFile(r);
      setDraft(r.value);
    } catch (e: any) {
      setError(e?.message || 'Read failed');
    }
  };

  const saveDraft = async () => {
    if (!activeFile && !newKey.trim()) return;
    const key = activeFile ? activeFile.key : newKey.trim();
    setSaving(true);
    setError(null);
    try {
      await adminRequest('POST', `/memory/scopes/${encodeURIComponent(scope)}/${encodeURIComponent(scopeId)}/${encodeURIComponent(key)}`, {
        content: draft,
      });
      await refreshList(scope, scopeId);
      // Re-open to refresh updated_at + value
      await openFile(key);
      if (!activeFile) setNewKey('');
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteFile = async () => {
    if (!activeFile) return;
    if (!window.confirm(`Delete "${activeFile.key}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await adminRequest('DELETE', `/memory/scopes/${encodeURIComponent(scope)}/${encodeURIComponent(scopeId)}/${encodeURIComponent(activeFile.key)}`);
      setActiveFile(null);
      setDraft('');
      await refreshList(scope, scopeId);
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setActiveFile(null);
    setDraft('');
    setNewKey('');
  };

  const idPickerOptions: { value: string; label: string }[] = (() => {
    if (scope === 'user') {
      return users.map((u) => ({
        value: u.id,
        label: `${u.display_name || '(unnamed)'} ${u.email ? `<${u.email}>` : ''}`.trim(),
      }));
    }
    if (scope === 'workspace') {
      return workspaces.map((w) => ({
        value: w.id,
        label: `${w.name} (${w.deck_count} deck${w.deck_count === 1 ? '' : 's'})`,
      }));
    }
    if (scope === 'task') {
      return decks.map((d) => ({ value: d.id, label: d.title.slice(0, 60) }));
    }
    // org → manual entry only
    return [];
  })();

  const cellStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 8,
    background: 'var(--bg-surface)', padding: 14,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
      <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
        Memory
      </h2>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: 0 }}>
        Four-scope agent memory store. Each scope holds key→value entries the agent reads + writes via <code>pp save memory</code>. Aligns PP main #141.
      </p>

      {/* Scope toggle */}
      <div style={{ display: 'flex', gap: 4, border: '1px solid var(--border)', borderRadius: 8, padding: 3, alignSelf: 'flex-start' }}>
        {(['task', 'user', 'workspace', 'org'] as MemoryScope[]).map((s) => (
          <button
            key={s}
            onClick={() => { setScope(s); setScopeId(''); setManualId(''); setActiveFile(null); setEntries([]); }}
            style={{
              padding: '5px 14px',
              fontSize: 'var(--fs-sm)',
              fontWeight: 500,
              background: scope === s ? 'var(--bg-accent, rgba(0,51,255,0.08))' : 'transparent',
              color: scope === s ? 'var(--brand, #0033FF)' : 'var(--text-secondary)',
              border: 'none', borderRadius: 6, cursor: 'pointer',
              fontFamily: 'inherit', textTransform: 'capitalize',
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#ef4444', fontSize: 'var(--fs-sm)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 12, alignItems: 'stretch' }}>
        {/* Left: scope-id picker + entries list */}
        <div style={{ ...cellStyle, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              {scope === 'task' ? 'Deck' : scope === 'user' ? 'User' : scope === 'workspace' ? 'Workspace' : 'Org id'}
            </div>
            {scope === 'org' ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="Type org id…"
                  style={{ flex: 1, padding: '6px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit' }}
                />
                <button
                  onClick={() => setScopeId(manualId.trim())}
                  disabled={!manualId.trim()}
                  style={{ padding: '6px 10px', fontSize: 'var(--fs-xs)', fontWeight: 600, background: 'var(--brand, #0033FF)', color: 'white', border: 'none', borderRadius: 6, cursor: manualId.trim() ? 'pointer' : 'not-allowed', opacity: manualId.trim() ? 1 : 0.55, fontFamily: 'inherit' }}
                >
                  Load
                </button>
              </div>
            ) : (
              <select
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit' }}
              >
                <option value="">— select —</option>
                {idPickerOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
              Entries
            </div>
            <button
              onClick={startNew}
              style={{ padding: '2px 8px', fontSize: 'var(--fs-xs)', fontWeight: 600, background: 'transparent', color: 'var(--brand, #0033FF)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}
              title="Create a new entry"
            >
              + New
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', minHeight: 200, maxHeight: 460 }}>
            {!scopeId ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', padding: 8 }}>
                Pick a {scope} above.
              </div>
            ) : loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', padding: 8 }}>
                <SpinnerGap size={12} className="animate-spin" /> Loading…
              </div>
            ) : entries.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', padding: 8 }}>
                No entries yet.
              </div>
            ) : (
              entries.map((e) => (
                <button
                  key={e.key}
                  onClick={() => void openFile(e.key)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 10px', fontSize: 'var(--fs-xs)',
                    background: activeFile?.key === e.key ? 'var(--bg-accent, rgba(0,51,255,0.06))' : 'transparent',
                    color: activeFile?.key === e.key ? 'var(--brand, #0033FF)' : 'var(--text-primary)',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'inherit', marginBottom: 1,
                  }}
                >
                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.key}</div>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                    {Math.max(1, Math.round(e.size / 1024))} KB · {new Date(e.updated_at).toLocaleDateString()}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div style={{ ...cellStyle, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 460 }}>
          {!scopeId ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
              Select a scope id on the left.
            </div>
          ) : activeFile === null && newKey === '' && entries.length > 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
              Pick an entry on the left, or click <strong>+ New</strong> to create one.
            </div>
          ) : (
            <>
              {activeFile ? (
                <div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                    Key
                  </div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                    {activeFile.key}
                  </div>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 4 }}>
                    {activeFile.content_type} · updated {new Date(activeFile.updated_at).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                    New key
                  </div>
                  <input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder="e.g. pitch-brief.json or preferences/tone.md"
                    style={{ width: '100%', padding: '6px 10px', fontSize: 'var(--fs-sm)', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'monospace', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Markdown / JSON / plain text…"
                style={{
                  flex: 1, minHeight: 280,
                  padding: 10, fontSize: 'var(--fs-sm)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--bg-primary)', color: 'var(--text-primary)',
                  fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                }}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => void saveDraft()}
                  disabled={saving || (!activeFile && !newKey.trim())}
                  style={{
                    padding: '6px 14px', fontSize: 'var(--fs-sm)', fontWeight: 600,
                    background: 'var(--brand, #0033FF)', color: 'white',
                    border: 'none', borderRadius: 8,
                    cursor: saving || (!activeFile && !newKey.trim()) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    opacity: saving || (!activeFile && !newKey.trim()) ? 0.55 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {activeFile && (
                  <button
                    onClick={() => void deleteFile()}
                    disabled={saving}
                    style={{ padding: '6px 14px', fontSize: 'var(--fs-sm)', background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectionsManager() {
  // Backed by /api/admin/settings. Sensitive values come back masked
  // ("abcd••••xy") on every GET; cleartext is only sent on PUT. The
  // mask is treated as a "no change" sentinel by the save handler so
  // refreshing the page + hitting Save without re-typing doesn't
  // overwrite the real value with the mask string.
  const [confluenceClientId, setConfluenceClientId] = useState('');
  const [confluenceClientSecret, setConfluenceClientSecret] = useState('');
  const [confluenceRedirectUri, setConfluenceRedirectUri] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  // Track whether the loaded value is a masked placeholder. Save
  // skips fields that still match the mask so we don't overwrite.
  const [maskedFields, setMaskedFields] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    api.getAdminSettings()
      .then((settings) => {
        const masked = new Set<string>();
        const setIf = (key: string, setter: (v: string) => void) => {
          const v = settings[key]?.value || '';
          setter(v);
          if (v.includes('••••')) masked.add(key);
        };
        setIf('confluence_client_id', setConfluenceClientId);
        setIf('confluence_client_secret', setConfluenceClientSecret);
        setIf('confluence_redirect_uri', setConfluenceRedirectUri);
        setIf('google_client_id', setGoogleClientId);
        setIf('google_client_secret', setGoogleClientSecret);
        setMaskedFields(masked);
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const updates: Record<string, string> = {};
      const pushIfChanged = (key: string, current: string) => {
        const trimmed = current.trim();
        if (!trimmed) return;
        // Skip masked placeholders so refreshing the page + Save
        // doesn't overwrite the real value with the mask string.
        if (maskedFields.has(key) && trimmed.includes('••••')) return;
        updates[key] = trimmed;
      };
      pushIfChanged('confluence_client_id', confluenceClientId);
      pushIfChanged('confluence_client_secret', confluenceClientSecret);
      pushIfChanged('confluence_redirect_uri', confluenceRedirectUri);
      pushIfChanged('google_client_id', googleClientId);
      pushIfChanged('google_client_secret', googleClientSecret);
      await api.updateAdminSettings(updates);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading settings...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>
        Connections
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* PP-CF: no Arx connection section. PP-CF runs on Cloudflare
            Workers with no arx coupling. Confluence + Google OAuth
            sections below cover the user-managed connector config. */}

        {/* Confluence OAuth */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Plugs size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>Confluence</span>
          </div>

          {/* Client ID */}
          <div style={{ marginBottom: 16 }}>
            <div style={labelStyle}>OAuth Client ID</div>
            <input
              value={confluenceClientId}
              onChange={e => setConfluenceClientId(e.target.value)}
              placeholder="From Atlassian Developer Console"
              style={inputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Client Secret */}
          <div style={{ marginBottom: 16 }}>
            <div style={labelStyle}>OAuth Client Secret</div>
            <input
              value={confluenceClientSecret}
              onChange={e => setConfluenceClientSecret(e.target.value)}
              placeholder="OAuth 2.0 (3LO) client secret"
              type="password"
              autoComplete="off"
              style={{ ...inputStyle, fontFamily: 'monospace' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; e.currentTarget.type = 'text'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.type = 'password'; }}
            />
          </div>

          {/* Redirect URI */}
          <div>
            <div style={labelStyle}>Redirect URI</div>
            <input
              value={confluenceRedirectUri}
              onChange={e => setConfluenceRedirectUri(e.target.value)}
              placeholder={`${window.location.origin}/api/auth/confluence/callback`}
              style={inputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
            <div style={{ marginTop: 4, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              Must match the callback URL in your Atlassian app
            </div>
          </div>
        </div>

        {/* Google OAuth */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Plugs size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>Google OAuth</span>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={labelStyle}>Client ID</div>
            <input
              value={googleClientId}
              onChange={e => setGoogleClientId(e.target.value)}
              placeholder="xxx.apps.googleusercontent.com"
              style={inputStyle}
              onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={labelStyle}>Client Secret</div>
            <input
              value={googleClientSecret}
              onChange={e => setGoogleClientSecret(e.target.value)}
              placeholder="Google OAuth client secret"
              type="password"
              autoComplete="off"
              style={{ ...inputStyle, fontFamily: 'monospace' }}
              onFocus={e => { e.currentTarget.style.borderColor = '#0033FF'; e.currentTarget.type = 'text'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.type = 'password'; }}
            />
          </div>

          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Redirect URI (auto-detected): <code style={{ fontSize: 'var(--fs-2xs)' }}>{window.location.origin}/api/auth/google/callback</code>
            <br />Add this to your Google Cloud Console Authorized redirect URIs.
          </div>
        </div>

        {/* Save button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {error && (
            <div style={{ fontSize: 'var(--fs-sm)', color: '#ef4444' }}>
              {error}
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 20px', fontSize: 'var(--fs-sm)', fontWeight: 600,
              background: 'var(--brand, #0033FF)', color: 'white', border: 'none', borderRadius: 8,
              cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
            }}
          >
            {saving ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : null}
            {saved ? 'Saved' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Configuration Manager ───────────────────────────────────────────────
//
// Skill API keys + global feature flags. Backed by /api/admin/settings.
// Sensitive values masked on GET; PUT accepts cleartext.
// Aligns PP main #131 ConfigurationManager (without the per-skill
// toggle UI — PP-CF binds talents via the Tulpas tab instead).

// Admin-managed global LLM provider keys. DeckDO.resolveAgentConfig
// reads these as the middle fallback between per-workspace BYO keys
// (Settings → Workspace AI) and the deploy-level env var. Setting one
// here lets every user run decks out of the box, while power users
// can still override at the workspace level.
const LLM_PROVIDER_KEYS: {
  key: string;
  label: string;
  help: string;
  signup_url: string;
  signup_label: string;
}[] = [
  {
    key: 'anthropic_api_key',
    label: 'Anthropic API key (global default)',
    help: 'Used by every workspace that has not configured its own key. Users can still override per-workspace under Settings → Workspace AI.',
    signup_url: 'https://console.anthropic.com/settings/keys',
    signup_label: 'Get Anthropic key',
  },
];

const SKILL_API_KEYS: {
  key: string;
  label: string;
  help: string;
  signup_url: string;
  signup_label: string;
}[] = [
  {
    key: 'unsplash_access_key',
    label: 'Unsplash access key',
    help: 'Required by pp-image-search to query Unsplash. Demo tier is free.',
    signup_url: 'https://unsplash.com/oauth/applications',
    signup_label: 'Get Unsplash key',
  },
  {
    key: 'pexels_api_key',
    label: 'Pexels API key',
    help: 'Required by pp-image-search to query Pexels. Free; no approval queue.',
    signup_url: 'https://www.pexels.com/api/',
    signup_label: 'Get Pexels key',
  },
  {
    key: 'brandfetch_token',
    label: 'Brandfetch token (optional)',
    help: 'Used by pp-brand-assets for higher-quality third-party logos. CDN works without a token.',
    signup_url: 'https://developers.brandfetch.com/dashboard',
    signup_label: 'Get Brandfetch key',
  },
];

const FEATURE_FLAGS: { key: string; label: string; help: string }[] = [
  {
    key: 'feature_speaker_notes_pane',
    label: 'Speaker Notes pane in Preview',
    help: 'Show the PowerPoint-style notes pane below the slide viewer.',
  },
  {
    key: 'feature_web_search',
    label: 'Agent web search',
    help: 'Allow the sandbox agent to use WebSearch for current facts, market research, and user-provided topics.',
  },
  {
    key: 'feature_web_fetch',
    label: 'Agent web fetch',
    help: 'Allow the published Agent to fetch user-provided URLs and source pages during research.',
  },
  {
    key: 'feature_confluence',
    label: 'Confluence research',
    help: 'Enable Confluence connection and page-reference workflows.',
  },
  {
    key: 'feature_droploft_publish',
    label: 'Droploft publish',
    help: 'Enable Droploft connection, reference, and publish workflows.',
  },
  {
    key: 'feature_image_search',
    label: 'Image search',
    help: 'Enable external image-search workflows for slide visuals.',
  },
  {
    key: 'feature_image_generation',
    label: 'Image generation',
    help: 'Enable AI-generated raster image workflows when provider credentials are configured.',
  },
  {
    key: 'feature_browser_export',
    label: 'Browser export',
    help: 'Enable browser-rendered export flows for HTML decks.',
  },
  {
    key: 'feature_agent_activity_stream',
    label: 'Agent activity stream',
    help: 'Show live agent progress events while a deck is being made.',
  },
  {
    key: 'feature_progressive_preview',
    label: 'Progressive preview',
    help: 'Refresh deck preview as each slide artifact is saved.',
  },
  {
    key: 'feature_session_resume',
    label: 'Agent session resume',
    help: 'Continue work inside the deck\'s Mosoo Thread between turns.',
  },
  {
    key: 'feature_artifact_validation',
    label: 'Artifact validation',
    help: 'Validate generated slides and manifests before presentation/export.',
  },
  {
    key: 'feature_asset_download',
    label: 'Asset download',
    help: 'Allow the agent to download and package external assets needed by a deck.',
  },
  {
    key: 'feature_external_publish',
    label: 'External publish',
    help: 'Allow publishing deck output to external systems beyond PitchPilot.',
  },
  {
    key: 'feature_silent_lint',
    label: 'Silent quality-gate retries',
    help: "When a gate fails (e.g. viewport overflow), the agent rewrites and re-runs without surfacing a checklist.",
  },
  {
    key: 'feature_gate11_viewport_fit',
    label: 'Gate 11 — viewport-fit lint',
    help: 'Run pp lint at end of Rehearse to catch slide overflow.',
  },
  {
    key: 'enable_google_login',
    label: 'Google login',
    help: 'Show "Sign in with Google" on the login page (requires Google OAuth config in Connections).',
  },
  {
    key: 'enable_register',
    label: 'Self-service registration',
    help: 'Allow new users to register. Off → only existing accounts work; admin must create users manually.',
  },
];

function ConfigurationManager() {
  const { wsId, ready: wsReady } = usePrimaryWorkspaceId();
  const [settings, setSettings] = useState<api.AdminSettings>({});
  const [tulpas, setTulpas] = useState<api.PpTulpa[]>([]);
  const [talents, setTalents] = useState<api.PpTalent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [pendingApiKeys, setPendingApiKeys] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, tulpaRows, talentRows] = await Promise.all([
        api.getAdminSettings(),
        wsReady && wsId ? api.listPpTulpas(wsId).catch(() => [] as api.PpTulpa[]) : Promise.resolve([] as api.PpTulpa[]),
        wsReady && wsId ? api.listPpTalents(wsId).catch(() => [] as api.PpTalent[]) : Promise.resolve([] as api.PpTalent[]),
      ]);
      setSettings(s);
      setTulpas(tulpaRows);
      setTalents(talentRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [wsId, wsReady]);

  useEffect(() => { void reload(); }, [reload]);

  const saveSetting = useCallback(async (key: string, value: string) => {
    setSavingKey(key);
    setSavedKey(null);
    try {
      const fresh = await api.updateAdminSettings({ [key]: value });
      setSettings(fresh);
      setPendingApiKeys(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSavedKey(key);
      setTimeout(() => setSavedKey(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingKey(null);
    }
  }, []);

  const writerTulpa = useMemo(
    () => tulpas.find(t => t.slug === 'pp-writer' && t.is_system) ?? tulpas.find(t => t.slug === 'pp-writer') ?? null,
    [tulpas],
  );
  const systemSkillTalents = useMemo(
    () => talents
      .filter(t => t.is_system && t.slug.startsWith('pp-'))
      .sort((a, b) => a.slug.localeCompare(b.slug)),
    [talents],
  );
  const writerTalentSlugs = useMemo(
    () => new Set(writerTulpa?.talents.map(t => t.slug) ?? []),
    [writerTulpa],
  );

  const toggleWriterTalent = useCallback(async (slug: string, enabled: boolean) => {
    if (!writerTulpa) return;
    setSavingKey(`talent:${slug}`);
    setSavedKey(null);
    try {
      const current = writerTulpa.talents.map(t => t.slug);
      const next = enabled
        ? Array.from(new Set([...current, slug]))
        : current.filter(s => s !== slug);
      await api.updateDefaultWriterTalentBindings(next);
      setSavedKey(`talent:${slug}`);
      setTimeout(() => setSavedKey(null), 2000);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingKey(null);
    }
  }, [reload, writerTulpa]);

  if (loading) {
    return (
      <div style={{ padding: 40, color: 'var(--text-muted)' }}>
        <SpinnerGap size={20} weight="bold" className="animate-spin" /> Loading configuration…
      </div>
    );
  }

  const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>
        {title}
      </h2>
      {hint && (
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: '0 0 12px' }}>{hint}</p>
      )}
      <div style={sectionStyle}>{children}</div>
    </section>
  );

  return (
    <div style={{ maxWidth: 880 }}>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>
        Configuration
      </h1>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', margin: '0 0 24px' }}>
        Global admin settings — API keys and feature flags. Changes apply to all decks at the next agent task-send.
      </p>

      {error && (
        <div style={{
          marginBottom: 16, padding: '10px 14px',
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 6, color: '#ef4444', fontSize: 'var(--fs-sm)',
        }}>
          {error}
        </div>
      )}

      <Section
        title="LLM Provider Keys"
        hint="Global default keys used by every deck agent. Users can override per-workspace under Settings → Workspace AI. Sensitive values are masked after save."
      >
        {LLM_PROVIDER_KEYS.map(({ key, label, help, signup_url, signup_label }) => {
          const current = settings[key]?.value || '';
          const pending = pendingApiKeys[key];
          const isMasked = current.includes('••••');
          const value = pending !== undefined ? pending : (isMasked ? '' : current);
          const isSaving = savingKey === key;
          const justSaved = savedKey === key;
          return (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {label}
                  {isMasked && pending === undefined && (
                    <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontWeight: 400 }}>
                      (set — re-enter to update)
                    </span>
                  )}
                </label>
                <a href={signup_url} target="_blank" rel="noreferrer noopener" style={{ fontSize: 'var(--fs-xs)', color: 'var(--brand, #0033FF)', textDecoration: 'none' }}>
                  {signup_label} ↗
                </a>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="password"
                  value={value}
                  placeholder={isMasked ? '••••••••' : 'sk-ant-...'}
                  onChange={(e) => setPendingApiKeys(p => ({ ...p, [key]: e.target.value }))}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', fontFamily: 'monospace' }}
                />
                <button
                  disabled={isSaving || pending === undefined || pending === ''}
                  onClick={() => void saveSetting(key, pending || '')}
                  style={{
                    padding: '8px 14px', borderRadius: 6,
                    background: justSaved ? '#22c55e' : 'var(--brand, #0033FF)',
                    color: '#fff', border: 'none',
                    cursor: (isSaving || pending === undefined || pending === '') ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--fs-sm)', fontWeight: 500,
                    opacity: (isSaving || pending === undefined || pending === '') ? 0.55 : 1,
                  }}
                >
                  {isSaving ? 'Saving…' : justSaved ? 'Saved' : 'Save'}
                </button>
              </div>
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                {help}
              </p>
            </div>
          );
        })}
      </Section>

      <Section
        title="Skill API Keys"
        hint="Injected into the agent at task-assign time. Skills like pp-image-search and pp-brand-assets read them via the runtime config. Sensitive values are masked after save."
      >
        {SKILL_API_KEYS.map(({ key, label, help, signup_url, signup_label }) => {
          const current = settings[key]?.value || '';
          const pending = pendingApiKeys[key];
          const isMasked = current.includes('••••');
          const value = pending !== undefined ? pending : (isMasked ? '' : current);
          const isSaving = savingKey === key;
          const justSaved = savedKey === key;
          return (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <label style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {label}
                  {isMasked && pending === undefined && (
                    <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontWeight: 400 }}>
                      (set — re-enter to update)
                    </span>
                  )}
                </label>
                <a href={signup_url} target="_blank" rel="noreferrer noopener" style={{ fontSize: 'var(--fs-xs)', color: 'var(--brand, #0033FF)', textDecoration: 'none' }}>
                  {signup_label} ↗
                </a>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="password"
                  value={value}
                  placeholder={isMasked ? '••••••••' : 'Enter key…'}
                  onChange={(e) => setPendingApiKeys(p => ({ ...p, [key]: e.target.value }))}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', fontFamily: 'monospace' }}
                />
                <button
                  disabled={isSaving || pending === undefined || pending === ''}
                  onClick={() => void saveSetting(key, pending || '')}
                  style={{
                    padding: '8px 14px', borderRadius: 6,
                    background: justSaved ? '#22c55e' : 'var(--brand, #0033FF)',
                    color: '#fff', border: 'none',
                    cursor: (isSaving || pending === undefined || pending === '') ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--fs-sm)', fontWeight: 500,
                    opacity: (isSaving || pending === undefined || pending === '') ? 0.55 : 1,
                  }}
                >
                  {isSaving ? 'Saving…' : justSaved ? 'Saved' : 'Save'}
                </button>
              </div>
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                {help}
              </p>
            </div>
          );
        })}
      </Section>

      <Section
        title="Feature Flags"
        hint="Global on/off switches. Toggling here takes effect immediately."
      >
        {FEATURE_FLAGS.map(({ key, label, help }) => {
          const value = settings[key]?.value || 'false';
          const on = value === 'true';
          const isSaving = savingKey === key;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 500, color: 'var(--text-primary)' }}>{label}</div>
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', margin: '2px 0 0' }}>{help}</p>
              </div>
              <button
                disabled={isSaving}
                onClick={() => void saveSetting(key, on ? 'false' : 'true')}
                style={{
                  width: 44, height: 24, borderRadius: 12,
                  border: 'none', cursor: isSaving ? 'wait' : 'pointer',
                  background: on ? 'var(--brand, #0033FF)' : 'var(--border-strong, #555)',
                  position: 'relative', flexShrink: 0,
                  transition: 'background 0.15s',
                  opacity: isSaving ? 0.5 : 1,
                }}
                aria-label={on ? 'Disable' : 'Enable'}
              >
                <span style={{
                  position: 'absolute', top: 2,
                  left: on ? 22 : 2,
                  width: 20, height: 20, borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.15s',
                }} />
              </button>
            </div>
          );
        })}
      </Section>

      <Section
        title="Skill Toggles (pp-writer)"
        hint="Admin-only bindings for the global system pp-writer. Only system talents are shown here."
      >
        {!wsReady ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
            <SpinnerGap size={16} weight="bold" className="animate-spin" /> Loading workspace...
          </div>
        ) : !wsId ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>No workspace is available for reading system talents.</div>
        ) : !writerTulpa ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>System pp-writer is not registered yet.</div>
        ) : systemSkillTalents.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>No system pp-* talents are available.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {systemSkillTalents.map(talent => {
              const on = writerTalentSlugs.has(talent.slug);
              const key = `talent:${talent.slug}`;
              const isSaving = savingKey === key;
              const justSaved = savedKey === key;
              return (
                <div
                  key={talent.slug}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: 12,
                    background: 'var(--bg-primary)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {talent.name || talent.slug}
                      </div>
                      {justSaved && (
                        <span style={{ fontSize: 'var(--fs-2xs)', color: '#22c55e', fontWeight: 700 }}>
                          Saved
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 6 }}>
                      {talent.slug}
                    </div>
                    {talent.description && (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                        {talent.description}
                      </div>
                    )}
                  </div>
                  <button
                    disabled={isSaving}
                    onClick={() => void toggleWriterTalent(talent.slug, !on)}
                    style={{
                      width: 44, height: 24, borderRadius: 12,
                      border: 'none', cursor: isSaving ? 'wait' : 'pointer',
                      background: on ? 'var(--brand, #0033FF)' : 'var(--border-strong, #555)',
                      position: 'relative', flexShrink: 0,
                      transition: 'background 0.15s',
                      opacity: isSaving ? 0.5 : 1,
                    }}
                    aria-label={on ? 'Disable skill' : 'Enable skill'}
                  >
                    <span style={{
                      position: 'absolute', top: 2,
                      left: on ? 22 : 2,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#fff',
                      transition: 'left 0.15s',
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Credentials Manager ─────────────────────────────────────────────────

interface Credential {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  category: string;
  key_names: string[];
  created_at: string;
}

interface CredentialCreateForm {
  name: string;
  display_name: string;
  description: string;
  category: string;
  secrets: { key: string; value: string }[];
}

function CredentialsManager() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [tulpas, setTulpas] = useState<Tulpa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CredentialCreateForm>({
    name: '', display_name: '', description: '', category: 'agent',
    secrets: [{ key: '', value: '' }],
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [credData, tulpaData] = await Promise.all([
        adminRequest<Credential[]>('GET', '/credentials'),
        adminRequest<Tulpa[]>('GET', '/tulpas/detail').catch(() => [] as Tulpa[]),
      ]);
      setCredentials(credData);
      setTulpas(tulpaData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const secrets: Record<string, string> = {};
      for (const row of form.secrets) {
        if (row.key.trim()) secrets[row.key.trim()] = row.value;
      }
      await adminRequest('POST', '/credentials', {
        name: form.name,
        display_name: form.display_name,
        description: form.description,
        category: form.category,
        secrets,
      });
      setCreating(false);
      setForm({ name: '', display_name: '', description: '', category: 'agent', secrets: [{ key: '', value: '' }] });
      await loadData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete credential "${name}"? This cannot be undone.`)) return;
    try {
      await adminRequest('DELETE', `/credentials/${id}`);
      await loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const addSecretRow = () => {
    setForm(f => ({ ...f, secrets: [...f.secrets, { key: '', value: '' }] }));
  };

  const removeSecretRow = (idx: number) => {
    setForm(f => ({ ...f, secrets: f.secrets.filter((_, i) => i !== idx) }));
  };

  const updateSecret = (idx: number, field: 'key' | 'value', val: string) => {
    setForm(f => ({
      ...f,
      secrets: f.secrets.map((s, i) => i === idx ? { ...s, [field]: val } : s),
    }));
  };

  const getTulpasUsingCredential = (credId: string): string[] => {
    return tulpas
      .filter(t => (t as any).credential_ids?.includes(credId))
      .map(t => t.name);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading credentials...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
          Credentials
        </h2>
        <button
          onClick={() => setCreating(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: 'var(--brand)', color: 'white',
            fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <Plus size={14} weight="bold" /> Create Credential
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 12, fontSize: 'var(--fs-sm)', color: '#ef4444' }}>{error}</div>
      )}

      {/* Create form */}
      {creating && (
        <div style={{ ...sectionStyle, marginBottom: 20 }}>
          <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
            New Credential
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={labelStyle}>Name</div>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. anthropic-key" style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Display Name</div>
              <input value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} placeholder="e.g. Anthropic API Key" style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Description</div>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Category</div>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="agent">agent</option>
                <option value="system">system</option>
              </select>
            </div>
            <div>
              <div style={labelStyle}>Secrets</div>
              {form.secrets.map((row, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input
                    value={row.key}
                    onChange={e => updateSecret(idx, 'key', e.target.value)}
                    placeholder="KEY_NAME"
                    style={{ ...inputStyle, flex: 1, fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}
                  />
                  <input
                    type="password"
                    value={row.value}
                    onChange={e => updateSecret(idx, 'value', e.target.value)}
                    placeholder="secret value"
                    style={{ ...inputStyle, flex: 2 }}
                  />
                  {form.secrets.length > 1 && (
                    <button
                      onClick={() => removeSecretRow(idx)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex' }}
                    >
                      <X size={14} weight="bold" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addSecretRow}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 6, border: '1px dashed var(--border)',
                  background: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                  fontSize: 'var(--fs-xs)', fontFamily: 'inherit',
                }}
              >
                <Plus size={12} weight="bold" /> Add Secret
              </button>
              <div style={{ marginTop: 6, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                Key name = environment variable name injected into agent container.
                Common: <code style={{ fontFamily: 'monospace', background: 'var(--bg-primary)', padding: '1px 4px', borderRadius: 3 }}>ANTHROPIC_API_KEY</code>,{' '}
                <code style={{ fontFamily: 'monospace', background: 'var(--bg-primary)', padding: '1px 4px', borderRadius: 3 }}>OPENAI_API_KEY</code>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                onClick={() => setCreating(false)}
                style={{
                  padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--bg-surface)', color: 'var(--text-secondary)',
                  fontSize: 'var(--fs-sm)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving || !form.name.trim()}
                style={{
                  padding: '6px 16px', borderRadius: 8, border: 'none',
                  background: 'var(--brand)', color: 'white',
                  fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', opacity: saving || !form.name.trim() ? 0.6 : 1,
                }}
              >
                {saving ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credential list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {credentials.map(cred => {
          const usedBy = getTulpasUsingCredential(cred.id);
          return (
            <div key={cred.id} style={{ ...sectionStyle, padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Key size={18} weight="light" style={{ color: 'var(--brand)' }} />
                  <div>
                    <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {cred.display_name || cred.name}
                    </div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                      {cred.name}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 'var(--fs-2xs)', fontWeight: 600,
                    background: cred.category === 'system' ? 'rgba(139,92,246,0.1)' : 'rgba(59,130,246,0.1)',
                    color: cred.category === 'system' ? '#8b5cf6' : '#3b82f6',
                  }}>
                    {cred.category}
                  </span>
                  <button
                    onClick={() => handleDelete(cred.id, cred.name)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', padding: 4, display: 'flex',
                    }}
                    onMouseOver={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                  >
                    <Trash size={16} weight="light" />
                  </button>
                </div>
              </div>
              {cred.description && (
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 8 }}>
                  {cred.description}
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {cred.key_names.map(k => (
                  <span key={k} style={{
                    padding: '2px 8px', borderRadius: 6, fontSize: 'var(--fs-2xs)',
                    background: 'var(--bg-primary)', color: 'var(--text-secondary)',
                    fontFamily: 'monospace', border: '1px solid var(--border)',
                  }}>
                    {k}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                <span>Created: {new Date(cred.created_at).toLocaleDateString()}</span>
                {usedBy.length > 0 && (
                  <span>Used by: <b style={{ color: 'var(--text-secondary)' }}>{usedBy.join(', ')}</b></span>
                )}
              </div>
            </div>
          );
        })}
        {credentials.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
            No credentials configured yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Users Manager ────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string | null;
  display_name: string;
  is_admin: number;
  created_at: number;
  // Backend returns `decks` (count of owned decks). Optional in the
  // type because older deployments may not have shipped the field yet.
  decks?: number;
  /** @deprecated kept for backward compat with the prior shape. */
  deck_count?: number;
}

function UsersManager() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Backend returns { users: AdminUser[] }; old shape returned the
      // array directly. Accept both so a mid-deploy refresh doesn't
      // crash the tab.
      const raw = await adminRequest<AdminUser[] | { users: AdminUser[] }>('GET', '/users');
      const list = Array.isArray(raw) ? raw : (raw?.users ?? []);
      setUsers(list);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const toggleAdmin = async (userId: string, currentIsAdmin: number) => {
    setTogglingId(userId);
    try {
      await adminRequest('PATCH', `/users/${userId}`, { is_admin: currentIsAdmin ? 0 : 1 });
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, is_admin: currentIsAdmin ? 0 : 1 } : u
      ));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading users...
      </div>
    );
  }

  if (error && users.length === 0) {
    return <div style={{ color: '#ef4444' }}>Error: {error}</div>;
  }

  return (
    <div>
      <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>
        Users
      </h2>
      {error && (
        <div style={{ marginBottom: 12, fontSize: 'var(--fs-sm)', color: '#ef4444' }}>
          {error}
        </div>
      )}
      <div style={{
        border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden',
        background: 'var(--bg-surface)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
              <th style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
              <th style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Decks</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</th>
              <th style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 16px', color: 'var(--text-primary)' }}>
                  {user.display_name || '(unnamed)'}
                </td>
                <td style={{ padding: '10px 16px', color: 'var(--text-secondary)', fontSize: 'var(--fs-xs)', wordBreak: 'break-all' }}>
                  {user.email || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                  {user.is_admin ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 10,
                      fontSize: 'var(--fs-2xs)', fontWeight: 600,
                      background: 'rgba(0,51,255,0.1)', color: 'var(--brand, #0033FF)',
                    }}>
                      <ShieldCheck size={12} weight="fill" /> Admin
                    </span>
                  ) : (
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>User</span>
                  )}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  {user.decks ?? user.deck_count ?? 0}
                </td>
                <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                  <button
                    onClick={() => toggleAdmin(user.id, user.is_admin)}
                    disabled={togglingId === user.id}
                    style={{
                      padding: '4px 10px', fontSize: 'var(--fs-xs)', fontWeight: 500,
                      background: user.is_admin ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                      color: user.is_admin ? '#ef4444' : '#16a34a',
                      border: `1px solid ${user.is_admin ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                      borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                      opacity: togglingId === user.id ? 0.5 : 1,
                    }}
                  >
                    {togglingId === user.id
                      ? <SpinnerGap size={12} className="animate-spin" />
                      : user.is_admin ? 'Revoke Admin' : 'Grant Admin'
                    }
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Agent Instance Manager ───────────────────────────────────────────────

interface ArxAgent {
  id: string;
  tulpa_id: string;
  tulpa_name?: string;
  program_status: string;
  container_name?: string;
  oasis_image?: string;
  oasis_ports?: Record<string, number>;
  last_activity?: string;
  created_at?: string;
}

function AgentsManager() {
  const [agents, setAgents] = useState<ArxAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminRequest<ArxAgent[]>('GET', '/arx-agents');
      setAgents(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleDestroy = async (agent: ArxAgent) => {
    if (!confirm(`Destroy agent ${agent.id.slice(0, 8)}...? This cannot be undone.`)) return;
    try {
      await adminRequest('DELETE', `/arx-agents/${agent.id}`);
      await loadAgents();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const statusColors: Record<string, { bg: string; color: string }> = {
    booting: { bg: 'rgba(234,179,8,0.1)', color: '#ca8a04' },
    idle: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6' },
    active: { bg: 'rgba(34,197,94,0.1)', color: '#16a34a' },
    offline: { bg: 'rgba(148,163,184,0.1)', color: '#94a3b8' },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading agents...
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
          Agents
        </h2>
        <button
          onClick={() => loadAgents()}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', color: 'var(--text-secondary)',
            fontSize: 'var(--fs-sm)', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <ArrowsClockwise size={14} weight="light" /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 12, fontSize: 'var(--fs-sm)', color: '#ef4444' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
        {agents.map(agent => {
          const sc = statusColors[agent.program_status] || statusColors.offline;
          return (
            <div key={agent.id} style={{ ...sectionStyle, padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Monitor size={18} weight="light" style={{ color: 'var(--brand)' }} />
                  <span style={{
                    fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-primary)',
                    fontFamily: 'monospace',
                  }}>
                    {agent.id.slice(0, 12)}...
                  </span>
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 'var(--fs-2xs)', fontWeight: 600,
                  background: sc.bg, color: sc.color,
                }}>
                  {agent.program_status}
                </span>
              </div>

              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 10 }}>
                {[
                  agent.tulpa_name && ['Tulpa', agent.tulpa_name],
                  agent.container_name && ['Container', agent.container_name],
                  agent.oasis_image && ['Image', agent.oasis_image],
                  agent.last_activity && ['Last Activity', new Date(agent.last_activity).toLocaleString()],
                  agent.oasis_ports && ['Ports', Object.entries(agent.oasis_ports).map(([k, v]) => `${k}:${v}`).join(', ')],
                ].filter(Boolean).map((entry) => {
                  const [k, v] = entry as [string, string];
                  return (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{k}</span>
                      <span style={{ color: 'var(--text-primary)', fontSize: 'var(--fs-xs)', fontFamily: 'monospace', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => handleDestroy(agent)}
                style={{
                  padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)',
                  background: 'none', color: 'var(--text-muted)',
                  fontSize: 'var(--fs-xs)', cursor: 'pointer', fontFamily: 'inherit',
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
              >
                Destroy
              </button>
            </div>
          );
        })}
        {agents.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', gridColumn: '1 / -1' }}>
            No running agent instances
          </div>
        )}
      </div>
    </div>
  );
}

// ── Decks Manager ────────────────────────────────────────────────────────

interface AdminDeck {
  id: string;
  title: string;
  phase: string;
  active_task: string | null;
  task_turn_id?: string | null;
  task_started_at?: number | null;
  task_last_progress_at?: number | null;
  task_error?: string | null;
  updated_at: number;
  owner_id: string;
  owner_name: string;
}

const phaseBadgeColors: Record<string, { bg: string; color: string }> = {
  plan: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6' },
  'plan-ready': { bg: 'rgba(234,179,8,0.1)', color: '#ca8a04' },
  preview: { bg: 'rgba(34,197,94,0.1)', color: '#16a34a' },
};

function DecksManager() {
  const [decks, setDecks] = useState<AdminDeck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDecks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Backend returns { decks: [...] } (current); the legacy
      // adminRequest unwrap (`data.data ?? data`) doesn't peel the
      // 'decks' field. Accept both shapes so the tab doesn't crash
      // on a 'data.map is not a function' from React.
      const raw = await adminRequest<AdminDeck[] | { decks: AdminDeck[] }>('GET', '/decks');
      const list = Array.isArray(raw) ? raw : (raw?.decks ?? []);
      setDecks(list);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDecks(); }, [loadDecks]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading decks...
      </div>
    );
  }

  if (error && decks.length === 0) {
    return <div style={{ color: '#ef4444' }}>Error: {error}</div>;
  }

  return (
    <div>
      <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>
        Decks
      </h2>
      {error && (
        <div style={{ marginBottom: 12, fontSize: 'var(--fs-sm)', color: '#ef4444' }}>
          {error}
        </div>
      )}
      <div style={{
        border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden',
        background: 'var(--bg-surface)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-primary)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Title</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Owner</th>
              <th style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phase</th>
              <th style={{ padding: '10px 16px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: 600, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {decks.map(deck => {
              const badge = phaseBadgeColors[deck.phase] || { bg: 'rgba(100,116,139,0.1)', color: '#64748b' };
              return (
                <tr key={deck.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--text-primary)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {deck.title || '(untitled)'}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-secondary)' }}>
                    {deck.owner_name || '-'}
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10,
                      fontSize: 'var(--fs-2xs)', fontWeight: 600,
                      background: badge.bg, color: badge.color,
                    }}>
                      {deck.phase || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                      {deck.active_task || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                    {deck.updated_at ? new Date(deck.updated_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              );
            })}
            {decks.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: '20px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
                  No decks found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Talent Manager ───────────────────────────────────────────────────────

// CF-native: workspace-scoped CRUD against /api/talents.
function TalentManager() {
  const [talents, setTalents] = useState<api.PpTalent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState<'prompt' | 'script' | null>(null);

  const { wsId, ready: wsReady } = usePrimaryWorkspaceId();

  const load = useCallback(async () => {
    if (!wsReady) return;
    if (!wsId) {
      setError('No workspace selected.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.listPpTalents(wsId);
      setTalents(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [wsId, wsReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (slug: string) => {
    if (!wsId) return;
    if (!confirm(`Delete talent "${slug}"?`)) return;
    try {
      await api.deletePpTalent(wsId, slug);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Talents</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setCreating('prompt')} style={primaryBtn}>+ Prompt talent</button>
          <button onClick={() => setCreating('script')} style={primaryBtn}>+ Script talent</button>
          <button onClick={() => void load()} title="Reload" style={ghostBtn}>
            <ArrowsClockwise size={14} weight="light" />
          </button>
        </div>
      </div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}><SpinnerGap size={14} /> Loading…</div>}
      {!loading && talents.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
          No talents yet. Create one to get started.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {talents.map((t) => (
          <div key={t.id} style={{ ...sectionStyle, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {t.name}{' '}
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', fontWeight: 400 }}>
                    {t.slug} · {t.kind}
                    {t.kind === 'script' ? ` (${t.runtime ?? 'python'} ${t.entry ?? 'main.py'})` : ''}
                    {t.is_system ? ' · system' : ''}
                  </span>
                </div>
                {t.description && (
                  <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {t.description}
                  </div>
                )}
                <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 4 }}>
                  {t.files.length} file{t.files.length === 1 ? '' : 's'}
                </div>
              </div>
              {!t.is_system && (
                <button onClick={() => void handleDelete(t.slug)} style={dangerGhost}>
                  <Trash size={14} weight="light" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {creating && wsId && (
        <CreateTalentModal
          kind={creating}
          workspaceId={wsId}
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function CreateTalentModal({
  kind, workspaceId, onClose, onCreated,
}: {
  kind: 'prompt' | 'script';
  workspaceId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [runtime, setRuntime] = useState('python');
  const [entry, setEntry] = useState('main.py');
  const [files, setFiles] = useState<File[]>([]);
  const [requirements, setRequirements] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleCreate() {
    setSaving(true);
    setError('');
    try {
      if (kind === 'prompt') {
        if (!slug.trim() || !body.trim()) {
          throw new Error('Slug and body are required.');
        }
        await api.createPromptTalent(workspaceId, {
          slug: slug.trim(),
          name: name.trim() || slug.trim(),
          body,
          description: description.trim() || null,
        });
      } else {
        if (!slug.trim() || files.length === 0) {
          throw new Error('Slug and at least one file are required.');
        }
        const uploads = files.map((f) => ({ path: f.name, file: f }));
        if (requirements.trim()) {
          uploads.push({
            path: 'requirements.txt',
            file: new globalThis.File([requirements], 'requirements.txt', {
              type: 'text/plain',
            }),
          });
        }
        await api.uploadScriptTalent(
          workspaceId,
          {
            slug: slug.trim(),
            name: name.trim() || slug.trim(),
            runtime,
            entry,
            description: description.trim() || null,
          },
          uploads,
        );
      }
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 600 }}>
            New {kind === 'prompt' ? 'prompt' : 'script'} talent
          </h3>
          <button onClick={onClose} style={ghostBtn}><X size={14} weight="light" /></button>
        </div>
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={fieldLabel}>
            Slug
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. ppt-master"
              style={inputStyle}
            />
          </label>
          <label style={fieldLabel}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabel}>
            Description
            <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
          </label>
          {kind === 'prompt' ? (
            <label style={fieldLabel}>
              Body (markdown)
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
              />
            </label>
          ) : (
            <>
              <label style={fieldLabel}>
                Runtime
                <input value={runtime} onChange={(e) => setRuntime(e.target.value)} style={inputStyle} />
              </label>
              <label style={fieldLabel}>
                Entry script
                <input value={entry} onChange={(e) => setEntry(e.target.value)} style={inputStyle} />
              </label>
              <label style={fieldLabel}>
                Files
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                />
              </label>
              <label style={fieldLabel}>
                requirements.txt (optional)
                <textarea
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                  rows={4}
                  placeholder="python-pptx==0.6.23"
                  style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
                />
              </label>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={() => void handleCreate()} disabled={saving} style={primaryBtn}>
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tulpa Manager ─────────────────────────────────────────────

function TulpaManager() {
  const [tulpas, setTulpas] = useState<api.PpTulpa[]>([]);
  const [talents, setTalents] = useState<api.PpTalent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<api.PpTulpa | null>(null);
  const [creating, setCreating] = useState(false);

  const { wsId, ready: wsReady } = usePrimaryWorkspaceId();

  const load = useCallback(async () => {
    if (!wsReady) return;
    if (!wsId) {
      setError('No workspace selected.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [t, tal] = await Promise.all([
        api.listPpTulpas(wsId),
        api.listPpTalents(wsId).catch(() => [] as api.PpTalent[]),
      ]);
      setTulpas(t);
      setTalents(tal);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [wsId, wsReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (slug: string) => {
    if (!wsId) return;
    if (!confirm(`Delete tulpa "${slug}"?`)) return;
    try {
      await api.deletePpTulpa(wsId, slug);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Tulpas</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setCreating(true)} style={primaryBtn}>+ New tulpa</button>
          <button onClick={() => void load()} title="Reload" style={ghostBtn}>
            <ArrowsClockwise size={14} weight="light" />
          </button>
        </div>
      </div>
      {error && <div style={errorStyle}>{error}</div>}
      {loading && (
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
          <SpinnerGap size={14} /> Loading…
        </div>
      )}
      {!loading && tulpas.length === 0 && (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
          No tulpas yet.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tulpas.map((t) => (
          <div key={t.id} style={{ ...sectionStyle, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {t.name}{' '}
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', fontWeight: 400 }}>
                    {t.slug}
                    {t.is_default ? ' · default' : ''}
                    {t.is_system ? ' · system' : ''}
                  </span>
                </div>
                {t.description && (
                  <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: 4 }}>
                    {t.description}
                  </div>
                )}
                <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 6 }}>
                  Talents: {t.talents.length === 0 ? 'none' : t.talents.map((b) => b.slug).join(', ')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {!t.is_system && (
                  <>
                    <button onClick={() => setEditing(t)} style={ghostBtn} title="Edit">
                      <PencilSimple size={14} weight="light" />
                    </button>
                    <button onClick={() => void handleDelete(t.slug)} style={dangerGhost}>
                      <Trash size={14} weight="light" />
                    </button>
                  </>
                )}
                {t.is_system && (
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>read-only</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {(creating || editing) && wsId && (
        <TulpaEditModal
          workspaceId={wsId}
          allTalents={talents}
          tulpa={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function TulpaEditModal({
  workspaceId, allTalents, tulpa, onClose, onSaved,
}: {
  workspaceId: string;
  allTalents: api.PpTalent[];
  tulpa: api.PpTulpa | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!tulpa;
  const [slug, setSlug] = useState(tulpa?.slug ?? '');
  const [name, setName] = useState(tulpa?.name ?? '');
  const [description, setDescription] = useState(tulpa?.description ?? '');
  const [systemPrompt, setSystemPrompt] = useState(tulpa?.system_prompt ?? '');
  const [talentSlugs, setTalentSlugs] = useState<string[]>(
    tulpa?.talents.map((b) => b.slug) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      if (!slug.trim() || !systemPrompt.trim()) {
        throw new Error('Slug and system prompt are required.');
      }
      if (isEdit) {
        await api.updatePpTulpa(workspaceId, tulpa!.slug, {
          name: name.trim(),
          system_prompt: systemPrompt,
          description: description.trim() || null,
          talent_slugs: talentSlugs,
        });
      } else {
        await api.createPpTulpa(workspaceId, {
          slug: slug.trim(),
          name: name.trim() || slug.trim(),
          system_prompt: systemPrompt,
          description: description.trim() || null,
          talent_slugs: talentSlugs,
        });
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function toggleTalent(s: string) {
    setTalentSlugs((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 600 }}>
            {isEdit ? `Edit tulpa: ${tulpa!.slug}` : 'New tulpa'}
          </h3>
          <button onClick={onClose} style={ghostBtn}><X size={14} weight="light" /></button>
        </div>
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isEdit && (
            <label style={fieldLabel}>
              Slug
              <input value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} placeholder="e.g. pp-writer" />
            </label>
          )}
          <label style={fieldLabel}>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabel}>
            Description
            <input value={description ?? ''} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
          </label>
          <label style={fieldLabel}>
            System prompt
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={14}
              style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical' }}
            />
          </label>
          <div>
            <div style={{ ...fieldLabel, marginBottom: 6 }}>Bound talents</div>
            {allTalents.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                No talents in this workspace yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 240, overflowY: 'auto' }}>
                {allTalents.map((t) => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-sm)' }}>
                    <input
                      type="checkbox"
                      checked={talentSlugs.includes(t.slug)}
                      onChange={() => toggleTalent(t.slug)}
                    />
                    <span>{t.name} <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-2xs)' }}>{t.slug} · {t.kind}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={() => void handleSave()} disabled={saving} style={primaryBtn}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── shared admin form styles ─────────────────────────────────

const primaryBtn: React.CSSProperties = {
  padding: '6px 12px', background: 'var(--brand, #0033FF)', color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 'var(--fs-sm)',
  fontFamily: 'inherit', fontWeight: 500,
};
const ghostBtn: React.CSSProperties = {
  padding: '6px 10px', background: 'none', color: 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
  fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
};
const dangerGhost: React.CSSProperties = {
  padding: '6px 10px', background: 'none', color: '#dc2626',
  border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
  fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
};
const errorStyle: React.CSSProperties = {
  padding: 10, marginBottom: 12, background: '#fef2f2',
  border: '1px solid #fecaca', borderRadius: 6,
  color: '#991b1b', fontSize: 'var(--fs-sm)',
};
const fieldLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
};
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
};
const modalCard: React.CSSProperties = {
  background: 'var(--bg-primary)', borderRadius: 12,
  padding: 20, width: 'min(640px, 92vw)', maxHeight: '85vh',
  overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
};


// ── Templates Manager ─────────────────────────────────────────────────

interface CustomTemplate {
  ref: string;
  templateId: string;
  baseName: string;
  title: string;
  description: string;
  author?: string;
  created_at?: number;
}

function TemplatesManager() {
  const [templates, setTemplates] = useState<CustomTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRef, setEditingRef] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingRef, setDeletingRef] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminRequest<CustomTemplate[]>('GET', '/templates');
      setTemplates(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const startEdit = (tpl: CustomTemplate) => {
    setEditingRef(tpl.ref);
    setEditTitle(tpl.title);
    setEditDesc(tpl.description || '');
  };

  const cancelEdit = () => {
    setEditingRef(null);
    setEditTitle('');
    setEditDesc('');
  };

  const saveEdit = async () => {
    if (!editingRef) return;
    setSaving(true);
    try {
      await adminRequest('PUT', `/templates/${encodeURIComponent(editingRef)}`, {
        title: editTitle,
        description: editDesc,
      });
      setTemplates(prev => prev.map(t =>
        t.ref === editingRef ? { ...t, title: editTitle, description: editDesc } : t
      ));
      setEditingRef(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ref: string) => {
    if (!confirm('Delete this template? Associated files will also be removed.')) return;
    setDeletingRef(ref);
    try {
      await adminRequest('DELETE', `/templates/${encodeURIComponent(ref)}`);
      setTemplates(prev => prev.filter(t => t.ref !== ref));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeletingRef(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Loading templates...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>
        Templates
      </h2>
      {error && (
        <div style={{ marginBottom: 12, fontSize: 'var(--fs-sm)', color: '#ef4444' }}>{error}</div>
      )}

      {templates.length === 0 ? (
        <div style={{
          ...sectionStyle, textAlign: 'center' as const, padding: '40px 24px',
          color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
        }}>
          <Blueprint size={32} weight="light" style={{ marginBottom: 8, opacity: 0.5 }} />
          <p>No custom templates published yet.</p>
          <p style={{ fontSize: 'var(--fs-xs)', marginTop: 4 }}>Publish a deck from the Decks view to create a template.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {templates.map(tpl => {
            const isEditing = editingRef === tpl.ref;
            const isDeleting = deletingRef === tpl.ref;
            return (
              <div key={tpl.ref} style={{
                ...sectionStyle,
                opacity: isDeleting ? 0.5 : 1, transition: 'opacity 0.15s',
              }}>
                {isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <div style={labelStyle}>Title</div>
                      <input
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        style={inputStyle}
                        autoFocus
                      />
                    </div>
                    <div>
                      <div style={labelStyle}>Description</div>
                      <textarea
                        value={editDesc}
                        onChange={e => setEditDesc(e.target.value)}
                        rows={3}
                        style={{ ...inputStyle, resize: 'vertical' as const, fontFamily: 'inherit' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button onClick={cancelEdit} style={{
                        padding: '6px 14px', fontSize: 'var(--fs-sm)',
                        background: 'var(--bg-primary)', color: 'var(--text-secondary)',
                        border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                        Cancel
                      </button>
                      <button onClick={saveEdit} disabled={saving} style={{
                        padding: '6px 14px', fontSize: 'var(--fs-sm)',
                        background: 'var(--brand, #0033FF)', color: 'white',
                        border: 'none', borderRadius: 6,
                        cursor: 'pointer', fontFamily: 'inherit',
                        opacity: saving ? 0.7 : 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        {saving && <SpinnerGap size={12} className="animate-spin" />}
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                        {tpl.title}
                      </div>
                      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 8 }}>
                        {tpl.description || 'No description'}
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                        <span>Ref: <code style={{ fontFamily: 'monospace' }}>{tpl.ref}</code></span>
                        {tpl.author && <span>Author: {tpl.author}</span>}
                        {tpl.created_at && <span>Created: {new Date(tpl.created_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => startEdit(tpl)} style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', fontSize: 'var(--fs-xs)',
                        background: 'var(--bg-primary)', color: 'var(--text-secondary)',
                        border: '1px solid var(--border)', borderRadius: 6,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                        <PencilSimple size={12} /> Edit
                      </button>
                      <button onClick={() => handleDelete(tpl.ref)} disabled={isDeleting} style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '5px 10px', fontSize: 'var(--fs-xs)',
                        background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}>
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Auth tab removed — RS256 keys are deploy-time file mounts, no UI config needed.

// ── System Manager ────────────────────────────────────────────────────

interface CheckResult {
  ok: boolean;
  latency_ms?: number;
  detail?: string;
}

interface SystemHealth {
  ok: boolean;
  checks: Record<string, CheckResult>;
  worker_env?: string;
  timestamp?: number;
}

function SystemManager() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHealth = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Backend returns the SystemHealth shape directly (no wrapper key);
      // adminRequest's `data.data ?? data` returns the same object. The
      // legacy shape was {pp, arx} — PP-CF runs on Cloudflare Workers
      // with no arx coupling, so we render the new {ok, checks} shape.
      const data = await adminRequest<SystemHealth>('GET', '/system/health');
      setHealth(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
        <SpinnerGap size={18} weight="light" className="animate-spin" /> Checking system health...
      </div>
    );
  }

  if (error) {
    return <div style={{ color: '#ef4444' }}>Error: {error}</div>;
  }

  if (!health) return null;

  // Map checks to display tiles. Stable label / order so a new check
  // added to the backend appears under its raw key.
  const knownLabels: Record<string, string> = {
    d1: 'D1 Database',
    r2: 'R2 Storage',
    anthropic: 'Anthropic API',
    bootstrap_token: 'Bootstrap Token',
  };
  const services: { label: string; ok: boolean; detail: string }[] = Object.entries(health.checks ?? {}).map(
    ([key, c]) => ({
      label: knownLabels[key] ?? key,
      ok: !!c?.ok,
      detail: c?.detail
        ? c.detail
        : c?.ok
        ? typeof c.latency_ms === 'number'
          ? `${c.latency_ms}ms`
          : 'OK'
        : 'Failed',
    }),
  );
  // Pin a worker-running tile so the user always sees one green tile
  // even when every check fails (otherwise the page reads as "all
  // dead" which obscures the actual Worker uptime signal).
  services.unshift({
    label: 'Worker',
    ok: true,
    detail: health.worker_env ? `env=${health.worker_env}` : 'running',
  });

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          System
        </h2>
        <button
          onClick={loadHealth}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 12px', fontSize: 'var(--fs-xs)',
            background: 'var(--bg-surface)', color: 'var(--text-secondary)',
            border: '1px solid var(--border)', borderRadius: 6,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
        {services.map(svc => (
          <div key={svc.label} style={{
            ...sectionStyle,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '24px 16px', textAlign: 'center' as const,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%', marginBottom: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: svc.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            }}>
              {svc.ok
                ? <CheckCircle size={24} weight="fill" style={{ color: '#22c55e' }} />
                : <XCircle size={24} weight="fill" style={{ color: '#ef4444' }} />
              }
            </div>
            <div style={{
              fontSize: 'var(--fs-sm)', fontWeight: 600,
              color: 'var(--text-primary)', marginBottom: 4,
            }}>
              {svc.label}
            </div>
            <div style={{
              fontSize: 'var(--fs-xs)', color: svc.ok ? '#16a34a' : '#ef4444',
            }}>
              {svc.detail}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
